import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiEvents, apiGet, ApiError } from '../api/client';
import type { User, UsageMeta } from '../api/types';
export const SourceContext = createContext<UsageMeta | null>(null);
const UserContext = createContext<User | null>(null);
export const useUser = () => useContext(UserContext);
export const pageNav = { assign: (url: string) => window.location.assign(url) };
const LAST_SSO = 'product.sso.at';
export function ssoUrl() {
  const portal = import.meta.env.VITE_PORTAL_ORIGIN;
  if (!portal) return null;
  const url = new URL('/api/portal/sso/authorize', portal);
  url.search = new URLSearchParams({ app: 'product', r: location.pathname + location.search }).toString();
  return url.toString();
}
export function RequireAuth({ children }: { children: ReactNode }) {
  const query = useQuery({ queryKey: ['current-user'], queryFn: ({ signal }) => apiGet<User>('/api/console/me', signal), staleTime: 30_000 });
  const [expired, setExpired] = useState(false);
  const [looped, setLooped] = useState(false);
  const [showWait, setShowWait] = useState(false);
  const unauthenticated = expired || (query.error instanceof ApiError && query.error.code === 'unauthenticated');
  // 等待延迟属于这一次身份确认；已打开的页面突然过期不能继承首次登录的等待状态。
  const waitingForIdentity = query.isPending && !unauthenticated;
  useEffect(() => {
    if (!waitingForIdentity) return;
    const timer = setTimeout(() => setShowWait(true), 1000);
    return () => clearTimeout(timer);
  }, [waitingForIdentity]);
  useEffect(() => apiEvents.onUnauthenticated(() => setExpired(true)), []);
  const login = ssoUrl();
  useEffect(() => {
    if (!unauthenticated || !login) return;
    // 回跳后仍然 401 时停下，避免 cookie 被拦等故障造成入口与后台无限往返。
    const now = Date.now();
    let last = 0;
    try { last = Number(sessionStorage.getItem(LAST_SSO)) || 0; } catch { /* 隐私模式可能禁止存储 */ }
    if (now - last < 30_000) { setLooped(true); return; }
    try { sessionStorage.setItem(LAST_SSO, String(now)); } catch { /* 同上 */ }
    pageNav.assign(login);
  }, [unauthenticated, login]);
  if (unauthenticated && !login) return <main className="login"><div className="login-card" role="alert">统一入口地址尚未配置，请联系管理员</div></main>;
  // SSO 是直接导航，始终不渲染跳转页；只有身份请求耗时超过一秒才显示等待。
  if (unauthenticated && !looped) return null;
  if (query.isPending) return showWait ? <main className="login"><div className="login-card"><p className="hint" role="status">正在确认登录状态…</p></div></main> : null;
  if (looped) return <main className="login"><div className="login-card" role="alert"><h1>登录没有成功</h1><p className="mt-8">从统一入口回来后仍未识别登录。请检查浏览器是否允许 cookie，或重新登录。</p><a className="btn btn-primary mt-16" href={login!}>重新登录</a></div></main>;
  if (query.isError) return <main className="login"><div className="login-card" role="alert">{query.error instanceof ApiError && query.error.code === 'forbidden' ? <h1>你没有产品分析的访问权限</h1> : <><h1>认证服务暂时不可用</h1><button className="btn mt-16" onClick={() => void query.refetch()}>重试</button></>}</div></main>;
  return <UserContext.Provider value={query.data}>{children}</UserContext.Provider>;
}
